import { useEffect, useState } from "react";
import { Button } from "@jobber/components/Button";
import { Content } from "@jobber/components/Content";
import { DataTable } from "@jobber/components/DataTable";
import { InputText } from "@jobber/components/InputText";
import DashboardTab from "../../components/Dashboard/DashboardTab";
import { showError } from "../../helpers";
import {
    getListen360ApiUsers,
    addListen360ApiUser,
    updateListen360ApiUserToken,
} from "../../services";
import { Listen360ApiUser } from "../../types/Listen360ApiUser";

function UserMapping() {
    const [users, setUsers] = useState<Listen360ApiUser[]>([]);
    const [newName, setNewName] = useState("");
    const [newToken, setNewToken] = useState("");
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [editToken, setEditToken] = useState("");

    useEffect(() => {
        getListen360ApiUsers()
            .then(setUsers)
            .catch((e: any) => showError("Error fetching users: " + e.message));
    }, []);

    const handleAddUser = async () => {
        if (!newName.trim() || !newToken.trim()) return;
        try {
            const user = await addListen360ApiUser(newName.trim(), newToken.trim());
            setUsers((prev) => [...prev, user]);
            setNewName("");
            setNewToken("");
        } catch (e: any) {
            showError("Error adding user: " + e.message);
        }
    };

    const handleSaveToken = async (userId: string) => {
        if (!editToken.trim()) return;
        try {
            await updateListen360ApiUserToken(userId, editToken.trim());
            setEditingUserId(null);
            setEditToken("");
        } catch (e: any) {
            showError("Error updating token: " + e.message);
        }
    };

    const handleCancelEdit = () => {
        setEditingUserId(null);
        setEditToken("");
    };

    return (
        <DashboardTab>
            <Content spacing="base">
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                        <InputText
                            placeholder="Name"
                            value={newName}
                            onChange={(value) => setNewName(value as string)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <InputText
                            placeholder="Token"
                            value={newToken}
                            onChange={(value) => setNewToken(value as string)}
                        />
                    </div>
                    <Button
                        label="Add User"
                        onClick={handleAddUser}
                    />
                </div>
                <DataTable.Container>
                    <DataTable.Table style={{ tableLayout: "fixed", width: "100%" }}>
                        <colgroup>
                            <col style={{ width: "25%" }} />
                            <col style={{ width: "50%" }} />
                            <col style={{ width: "25%" }} />
                        </colgroup>
                        <DataTable.Body>
                            <DataTable.Row>
                                <DataTable.Cell><strong>Name</strong></DataTable.Cell>
                                <DataTable.Cell><strong>Token</strong></DataTable.Cell>
                                <DataTable.Cell></DataTable.Cell>
                            </DataTable.Row>
                            {users.map((user) => (
                                <DataTable.Row key={user.id}>
                                    <DataTable.Cell><strong>{user.name}</strong></DataTable.Cell>
                                    <DataTable.Cell>
                                        {editingUserId === user.id ? (
                                            <InputText
                                                placeholder="New token"
                                                value={editToken}
                                                onChange={(value) => setEditToken(value as string)}
                                            />
                                        ) : (
                                            <span style={{ fontFamily: "monospace" }}>{user.token_preview || "--"}</span>
                                        )}
                                    </DataTable.Cell>
                                    <DataTable.Cell>
                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            {editingUserId === user.id ? (
                                                <div style={{ display: "flex", gap: "8px" }}>
                                                    <Button
                                                        label="Save"
                                                        onClick={() => handleSaveToken(user.id)}
                                                        size="small"
                                                    />
                                                    <Button
                                                        label="Cancel"
                                                        onClick={handleCancelEdit}
                                                        type="secondary"
                                                        size="small"
                                                    />
                                                </div>
                                            ) : (
                                                <Button
                                                    label="Edit"
                                                    onClick={() => {
                                                        setEditingUserId(user.id);
                                                        setEditToken("");
                                                    }}
                                                    type="secondary"
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                    </DataTable.Cell>
                                </DataTable.Row>
                            ))}
                        </DataTable.Body>
                    </DataTable.Table>
                </DataTable.Container>
            </Content>
        </DashboardTab>
    );
}

export default UserMapping;
